/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  faCopy,
  faCrosshairs,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faExpand
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faLocationPin, faTruckFront } from '@fortawesome/pro-solid-svg-icons';
import 'leaflet';
import 'leaflet.markercluster';
import { TaskStatus } from '../../../enums/task-status.enum';
import { TracingModel } from '../../../models/record/record-base.model';
import { TaskStateLocationData } from '../../home/task-home/task/task-detail/task-detail.component';
import {
  CustomDateFormatService,
  DateFormat
} from '../../../services/custom-date-format/custom-date-format.service';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss',
  standalone: false
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {
  constructor(public customDateFormatService: CustomDateFormatService) {}
  readonly faCopy = faCopy;
  readonly faCrosshairs = faCrosshairs;
  readonly faMagnifyingGlassMinus = faMagnifyingGlassMinus;
  readonly faMagnifyingGlassPlus = faMagnifyingGlassPlus;
  readonly faExpand = faExpand;

  readonly faLocationPin = faLocationPin;
  readonly faCheck = faCheck;
  readonly faTruck = faTruckFront;

  private markerSize: number = 42;
  private readonly pinWidth = this.markerSize;
  private readonly pinHeight = this.markerSize * (faLocationPin.icon[0] / faLocationPin.icon[1]);
  private readonly overlayHeight = this.pinHeight * 0.4;
  private readonly overlayTop = this.pinHeight * 0.2;

  readonly locationPin = {
    path: faLocationPin.icon[4],
    transform: this.getCenteredTransform(faLocationPin, this.pinWidth, this.pinHeight, 0)
  };

  readonly truck = {
    path: faTruckFront.icon[4],
    transform: this.getCenteredTransform(
      faTruckFront,
      this.pinWidth,
      this.overlayHeight,
      this.overlayTop
    )
  };

  readonly check = {
    path: faCheck.icon[4],
    transform: this.getCenteredTransform(
      faCheck,
      this.pinWidth,
      this.overlayHeight,
      this.overlayTop
    )
  };

  get legendOverlay() {
    return this.taskStatus === TaskStatus.CLOSED ? this.check : this.truck;
  }

  private getCenteredTransform(
    icon: IconDefinition,
    containerWidth: number,
    targetHeight: number,
    top: number
  ): string {
    const sourceWidth = icon.icon[0];
    const sourceHeight = icon.icon[1];
    const scale = targetHeight / sourceHeight;
    const renderedWidth = sourceWidth * scale;
    const left = (containerWidth - renderedWidth) / 2;
    return `matrix(${scale} 0 0 ${scale} ${left} ${top})`;
  }

  private map!: globalThis.L.Map;
  @ViewChild('leafletmap') leafletmap!: ElementRef;
  private mapInitialized: boolean = false;
  @Input() location: TracingModel | undefined;
  @Input() taskStatus: TaskStatus = TaskStatus.NEW;
  @Input() taskStatesLocation: TaskStateLocationData[] = [];

  @Input() panDisabled: boolean = false;
  @Input() zoomDisabled: boolean = false;

  readonly TaskStatus = TaskStatus;
  private markerCluster = this.newMarkerCluster();
  private nonClusteringMarkers = new globalThis.L.FeatureGroup();

  initMap(): void {
    if (this.mapInitialized) return;
    this.resetMarkers();
    if (this.leafletmap) {
      this.map = globalThis.L.map(this.leafletmap.nativeElement, {
        zoomControl: false,
        boxZoom: false,
        doubleClickZoom: false,
        scrollWheelZoom: !this.zoomDisabled,
        touchZoom: !this.zoomDisabled,
        dragging: !this.panDisabled
      });
      this.mapInitialized = true;

      const tiles = globalThis.L.tileLayer(
        'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        {
          maxZoom: 18,
          attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }
      );

      tiles.addTo(this.map);
      this.updateMap();
    }
  }

  updateMap(): void {
    for (var stateLocation of this.taskStatesLocation) {
      if (!stateLocation.location) continue;
      var tooltip: string = stateLocation.name ?? stateLocation.type;
      if (stateLocation.timestamp) {
        tooltip = tooltip.concat(
          '\n',
          this.customDateFormatService.formatDate(stateLocation.timestamp, DateFormat.short)
        );
      }
      this.markerCluster.addLayer(
        globalThis.L.marker([stateLocation.location.latitude, stateLocation.location.longitude], {
          title: tooltip,
          icon: this._getMapPin('#ffffff', '#247963', stateLocation.icon)
        })
      );
    }
    const latValue = this.location?.last_known_location?.lat;
    const longValue = this.location?.last_known_location?.long;
    if (Number.isFinite(latValue) && Number.isFinite(longValue)) {
      const lat = latValue as number;
      const long = longValue as number;
      let tooltip: string =
        this.taskStatus == TaskStatus.CLOSED
          ? $localize`:@@lastKnownPosition:Last registered position`
          : $localize`:@@currentUserPosition:Current user position`;
      tooltip = tooltip.concat(
        '\n',
        this.customDateFormatService.formatDate(
          this.location?.last_known_location?.timestamp?.toString() ?? '',
          DateFormat.short
        )
      );
      this.nonClusteringMarkers.addLayer(
        globalThis.L.marker([lat, long], {
          icon: this._getMapPin(
            '#247963',
            '#ffffff',
            this.taskStatus == TaskStatus.CLOSED ? faCheck : faTruckFront
          ),
          title: tooltip
        })
      );
    }
    this.map.addLayer(this.nonClusteringMarkers);
    this.map.addLayer(this.markerCluster);
    this.zoomToFitAllMarkers();
  }

  ngAfterViewInit(): void {
    this.initMap();
    setTimeout(() => {
      if (!this.mapInitialized || !this.map) {
        return;
      }

      this.map.invalidateSize();
      this.zoomToFitAllMarkers();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['location'] || changes['taskStatesLocation']) {
      if (this.mapInitialized && this.map) {
        if (this.map.hasLayer(this.markerCluster)) {
          this.map.removeLayer(this.markerCluster);
        }
        if (this.map.hasLayer(this.nonClusteringMarkers)) {
          this.map.removeLayer(this.nonClusteringMarkers);
        }
        this.resetMarkers();
        this.updateMap();
        this.map.invalidateSize();
      } else {
        this.initMap();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.mapInitialized && this.map) {
      this.map.remove();
      this.mapInitialized = false;
    }
  }

  private resetMarkers(): void {
    this.markerCluster = this.newMarkerCluster();
    this.nonClusteringMarkers = new globalThis.L.FeatureGroup();
  }

  private newMarkerCluster() {
    return globalThis.L.markerClusterGroup({
      iconCreateFunction: (cluster) =>
        this._getMapPin('#ffffff', '#247963', undefined, cluster.getChildCount()),
      showCoverageOnHover: false,
      maxClusterRadius: this.markerSize * 0.8333333
    });
  }

  public focusLastPosition(): void {
    const latValue = this.location?.last_known_location?.lat;
    const longValue = this.location?.last_known_location?.long;
    if (Number.isFinite(latValue) && Number.isFinite(longValue)) {
      const lat = latValue as number;
      const long = longValue as number;
      this.map.setView([lat, long], 15, { animate: true });
    }
  }

  public zoomIn(): void {
    this.map.setZoom(this.map.getZoom() + 1);
  }

  public zoomOut(): void {
    this.map.setZoom(this.map.getZoom() - 1);
  }

  public zoomToFitAllMarkers(): void {
    var bounds = this.nonClusteringMarkers.getBounds().extend(this.markerCluster.getBounds());
    if (bounds.isValid()) {
      this.map.fitBounds(bounds, { maxZoom: 15, paddingTopLeft: [0, this.markerSize] });
    }
  }

  _getMapPin(
    pinColor: string,
    overlayColor: string,
    overlayIcon?: IconDefinition,
    count?: number
  ): globalThis.L.DivIcon {
    var hasCount = count !== undefined;
    var pinTransform = this.getCenteredTransform(faLocationPin, this.pinWidth, this.pinHeight, 0);
    var overlayPath = '';

    if (!hasCount && overlayIcon) {
      var overlayTransform = this.getCenteredTransform(
        overlayIcon,
        this.pinWidth,
        this.overlayHeight,
        this.overlayTop
      );
      overlayPath =
        '<path fill="' +
        overlayColor +
        '" d="' +
        overlayIcon.icon[4] +
        '" transform="' +
        overlayTransform +
        '"></path>';
    }

    var pinHtml =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' +
      this.pinWidth +
      ' ' +
      this.pinHeight +
      '" width="' +
      this.pinWidth +
      '" height="' +
      this.pinHeight +
      '">' +
      '<path fill="' +
      pinColor +
      '" d="' +
      this.locationPin.path +
      '" transform="' +
      pinTransform +
      '"></path>' +
      overlayPath +
      '</svg>';

    if (hasCount) {
      pinHtml =
        '<div style="position: relative;>' +
        '<div style="position: absolute; top: 0; left: 0;>' +
        pinHtml +
        '</div>' +
        '<div style="position: absolute; top: 0px; width: 100%; text-align: center; font-size: ' +
        0.38 * this.markerSize +
        'px; font-weight: bold; color: ' +
        overlayColor +
        '">' +
        count +
        '</div>' +
        '</div>';
    }

    return globalThis.L.divIcon({
      html: pinHtml,
      className: '',
      iconSize: [this.pinWidth, this.pinHeight],
      iconAnchor: [this.pinWidth / 2, this.pinHeight]
    });
  }
}
